// TODO: Agregar preferencias_tablas a types/database.ts cuando se regeneren tipos
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

/**
 * IMPORTANTE: Con Auth0 Third-Party Auth, no se debe usar `supabase.auth.getUser()`.
 * En su lugar, el userId debe provenir de Auth0 (user.sub) y pasarse como parámetro.
 *
 * Además, el cliente Supabase debe pasarse desde el contexto para que incluya
 * el token de Auth0 en cada request.
 *
 * Uso correcto:
 * ```typescript
 * const { user } = useAuth0()
 * const { supabase } = useSupabase()
 * const userId = user?.sub
 * if (userId && supabase) {
 *   await tablePreferencesService.get(supabase, userId, tableKey, organizacionId)
 * }
 * ```
 */

export interface TablePreferencesRecord {
  id: string
  user_id: string
  organizacion_id: string | null
  table_key: string
  visibility: Record<string, boolean>
  column_sizing: Record<string, number> | null
  created_at: string
  updated_at: string
}

export interface TablePreferencesInput {
  visibility?: Record<string, boolean>
  columnSizing?: Record<string, number>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyTable = any

/** Tipo de retorno normalizado con columnSizing en camelCase */
export interface TablePreferencesResponse {
  id: string
  user_id: string
  organizacion_id: string | null
  table_key: string
  visibility: Record<string, boolean>
  columnSizing: Record<string, number> | null
  created_at: string
  updated_at: string
}

export const tablePreferencesService = {
  /**
   * Obtiene las preferencias de una tabla para el usuario especificado
   *
   * @param client - Cliente Supabase autenticado (desde useSupabase)
   * @param userId - El user ID de Auth0 (user.sub)
   * @param tableKey - La clave de la tabla
   * @param organizacionId - (Opcional) ID de la organización
   */
  async get(
    client: SupabaseClient<Database> | null,
    userId: string,
    tableKey: string,
    organizacionId?: string
  ): Promise<TablePreferencesResponse | null> {
    if (!client || !userId) return null

    let query = (client.from('preferencias_tablas') as AnyTable)
      .select('*')
      .eq('user_id', userId)
      .eq('table_key', tableKey)

    if (organizacionId) {
      query = query.eq('organizacion_id', organizacionId)
    } else {
      query = query.is('organizacion_id', null)
    }

    const { data, error } = await query.maybeSingle()

    if (error) {
      console.error('Error obteniendo preferencias de tabla:', error)
      return null
    }

    if (!data) return null

    // Mapear column_sizing (snake_case de BD) a columnSizing (camelCase)
    return {
      ...data,
      columnSizing: data.column_sizing,
    } as TablePreferencesResponse
  },

  /**
   * Guarda o actualiza las preferencias de una tabla
   * Soporta actualización parcial (solo visibility, solo columnSizing, o ambos)
   *
   * @param client - Cliente Supabase autenticado (desde useSupabase)
   * @param userId - El user ID de Auth0 (user.sub)
   * @param tableKey - La clave de la tabla
   * @param preferences - Las preferencias a guardar
   * @param organizacionId - (Opcional) ID de la organización
   */
  async upsert(
    client: SupabaseClient<Database> | null,
    userId: string,
    tableKey: string,
    preferences: TablePreferencesInput,
    organizacionId?: string
  ): Promise<TablePreferencesResponse | null> {
    if (!client || !userId) return null

    // Primero obtenemos el registro existente para hacer merge
    const existing = await this.get(client, userId, tableKey, organizacionId)

    // Construir objeto de upsert con merge de preferencias
    const upsertData: Record<string, unknown> = {
      user_id: userId,
      organizacion_id: organizacionId || null,
      table_key: tableKey,
      updated_at: new Date().toISOString(),
    }

    // Merge visibility: si viene en preferences, usar; si no, mantener existente
    if (preferences.visibility !== undefined) {
      upsertData.visibility = preferences.visibility
    } else if (existing?.visibility) {
      upsertData.visibility = existing.visibility
    } else {
      upsertData.visibility = {}
    }

    // Merge columnSizing: si viene en preferences, usar; si no, mantener existente
    if (preferences.columnSizing !== undefined) {
      upsertData.column_sizing = preferences.columnSizing
    } else if (existing?.columnSizing) {
      upsertData.column_sizing = existing.columnSizing
    }

    const { data, error } = await (client.from('preferencias_tablas') as AnyTable)
      .upsert(upsertData, {
        onConflict: 'user_id,organizacion_id,table_key',
        ignoreDuplicates: false,
      })
      .select()
      .single()

    if (error) {
      console.error('Error guardando preferencias de tabla:', error)
      throw error
    }

    // Mapear respuesta a formato camelCase
    return {
      ...data,
      columnSizing: data.column_sizing,
    } as TablePreferencesResponse
  },

  /**
   * Elimina las preferencias de una tabla
   *
   * @param client - Cliente Supabase autenticado (desde useSupabase)
   * @param userId - El user ID de Auth0 (user.sub)
   * @param tableKey - La clave de la tabla
   * @param organizacionId - (Opcional) ID de la organización
   */
  async delete(
    client: SupabaseClient<Database> | null,
    userId: string,
    tableKey: string,
    organizacionId?: string
  ): Promise<void> {
    if (!client || !userId) return

    let query = (client.from('preferencias_tablas') as AnyTable)
      .delete()
      .eq('user_id', userId)
      .eq('table_key', tableKey)

    if (organizacionId) {
      query = query.eq('organizacion_id', organizacionId)
    } else {
      query = query.is('organizacion_id', null)
    }

    const { error } = await query

    if (error) {
      console.error('Error eliminando preferencias de tabla:', error)
      throw error
    }
  },

  /**
   * Obtiene todas las preferencias de tablas del usuario
   *
   * @param client - Cliente Supabase autenticado (desde useSupabase)
   * @param userId - El user ID de Auth0 (user.sub)
   */
  async getAll(
    client: SupabaseClient<Database> | null,
    userId: string
  ): Promise<TablePreferencesRecord[]> {
    if (!client || !userId) return []

    const { data, error } = await (client.from('preferencias_tablas') as AnyTable)
      .select('*')
      .eq('user_id', userId)

    if (error) {
      console.error('Error obteniendo todas las preferencias:', error)
      return []
    }

    return (data || []) as TablePreferencesRecord[]
  },
}
